import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, TextField, useTheme } from "@mui/material";

import { aggOperatorsByType } from "system/query/Aggs";
import { filterQueryFields, QueryField, QueryInfo, sortFieldsByTypeAndLabel } from "system/query/QueryInfo";
import { ColumnDialogMode, ColumnModelItem, ColumnRenderType } from "./ColumnModel";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { DraftColumn } from "components/widgets/tables/DataTable/DataTableSettings";

export type CustomColumnFieldType = {
  id: string,
  label: string,
  renderType?: ColumnModelItem["renderType"],
  type: string,
  _social_field_category?: string,
  showRenderTypeInSettings?: boolean,
};

interface DataTableColumnSelectorProps {
  activeColumnIndex: number | undefined;
  additionalFields: CustomColumnFieldType[];
  additionalFieldOps?: Record<string, { id: string, label: string }[]>;
  columnDialogMode: ColumnDialogMode | undefined;
  draftColumn: DraftColumn | undefined;
  localCategory: QueryField | undefined;
  onSetDraftColumn: (draftColumn: DraftColumn) => void;
  queryInfo: QueryInfo;
}

export default function DataTableColumnSelector(props: DataTableColumnSelectorProps) {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const fields = filterQueryFields(
    props.queryInfo.fields.filter(f => f.id !== "rand"),
    { allowedFieldTypes: ["decimal", "integer"] }
  );

  const sortedFields = sortFieldsByTypeAndLabel(fields);
  // The API results field for records is "_count" but the query field for records is "__count__"
  // The updatedFields is so that users can selected records in the dropdown and have the table display data 
  const updatedFields = sortedFields.map((fieldItem: QueryField) => {
    return fieldItem.id === "__count__" ?
      {
        ...fieldItem,
        id: "_count"
      }
      :
      fieldItem;
  });
  const finalFields: (QueryField | CustomColumnFieldType)[] = [...updatedFields, ...(props.additionalFields && props.additionalFields)];
  
  // Determines if field is member of the original API fields list
  const isOriginalField = (fieldId: string | undefined) => {
    return fields.some(field => field.id === fieldId);
  };

  const [selectedField, setSelectedField] = useState<QueryField | CustomColumnFieldType>(
    finalFields.find((f: QueryField | CustomColumnFieldType) => f.id === props.draftColumn?.id) ||
    finalFields[0] ||
    null
  );

  // The list of options for each query field, or for the custom fields
  const visualizationOptions = selectedField && isOriginalField(selectedField.id)
    ? aggOperatorsByType((selectedField as QueryField), i18n.t, props.queryInfo.supported_aggregation_ops)
    : selectedField ? props.additionalFieldOps?.[selectedField.id] : [];

  // The active selected option of the visualization options list
  const [selectedLocalRenderType, setSelectedLocalRenderType] = useState<{ id: string, label: string } | null>(() => {
    if (!visualizationOptions || visualizationOptions.length === 0) {
      return null;
    }

    // If draft column id is an API field, get the correlated operator options:
    // min, max, avg, sum, etc
    // Otherwise use the custom provided render type options
    if (isOriginalField(props.draftColumn?.id)) {
      const match = visualizationOptions.find(v => v.id === props.draftColumn?.operator);
      if (match) return match;
    } else {
      const match = visualizationOptions.find(v => v.id === props.draftColumn?.renderType);
      if (match) return match;
    }

    return (
      visualizationOptions.find(v => v.id === "avg") || 
      visualizationOptions[0] || 
      null
    );
  });

  function handleFieldChange(field: QueryField | CustomColumnFieldType) {
    if (!field) {
      setSelectedLocalRenderType(null);
      return;
    };

    // Count & Social Universe don't need operators
    const hasSubOption = !["_count", "social_universe"].includes(field.id);
    // If API field, it's "operator", if customer field it's "renderType"
    const keyType = isOriginalField(field.id) ? "operator" : "renderType";
    // What are the sub options based on whether field is API or custom?
    const nextOptions = (isOriginalField(field.id)
      ? aggOperatorsByType((field as QueryField), i18n.t, props.queryInfo.supported_aggregation_ops)
      : props.additionalFieldOps?.[field.id] || []);
    const subOption = nextOptions?.find((o: { id: string, label: string }) => o.id === "avg") || nextOptions?.[0] || null;
    let renderType = ColumnRenderType.Number;

    if ("renderType" in field && field.renderType) {
      renderType = field.renderType;
    }

    let newDraftColumn = {
      id: field.id,
      label: field.label,
      ...("showRenderTypeInSettings" in field && field.showRenderTypeInSettings !== undefined && {
        showRenderTypeInSettings: field.showRenderTypeInSettings
      }),
      ...((hasSubOption && subOption) && {
        [keyType]: subOption.id
      }),
      ...(renderType && { renderType })
    };

    if (props.columnDialogMode === ColumnDialogMode.UPDATE_COLUMN && field) {
      newDraftColumn = {
        ...newDraftColumn,
        ...((props.activeColumnIndex !== null && props.activeColumnIndex !== undefined) && {
          activeColumnIndex: props.activeColumnIndex
        }),
      };
    }

    setSelectedLocalRenderType(subOption);
    setSelectedField(field);
    props.onSetDraftColumn(newDraftColumn);
  }

  return (
    <>
      <Autocomplete
        options={props.localCategory?.options ? finalFields : finalFields.filter(f => f.id !== "ratio")}
        value={selectedField}
        groupBy={(option) => option._social_field_category || option.type}
        onChange={(e, field: QueryField | CustomColumnFieldType | null) => {
          if (field) {
            handleFieldChange(field);
          }
        }}
        renderInput={params => <TextField {...params} />}
        renderGroup={(params) => (
          <Box key={params.key}>
            <Box sx={{ fontWeight: "bold", p: 1, backgroundColor: theme.palette.background.default }}>
              {i18n.t([`query-social-field-categories.${params.group}`, params.group])}
            </Box>
            <ul>{params.children}</ul>
          </Box>
        )}
      />
      {!["_count", "social_universe"].includes(selectedField.id) && visualizationOptions && (
        <>
          {isOriginalField(selectedField?.id) ?
            <SimpleFormLabel>{i18n.t("filterable-table.operator")}</SimpleFormLabel> :
            <SimpleFormLabel>{i18n.t("tables.display-type")}</SimpleFormLabel>
          }
          <Autocomplete
            options={visualizationOptions ?? []}
            value={selectedLocalRenderType}
            defaultValue={selectedLocalRenderType}
            renderInput={params => <TextField {...params} />}
            onChange={(e, type: { id: string, label: string } | null) => {
              if (type) {
                setSelectedLocalRenderType(type);
              }

              if (props.draftColumn?.id) {
                const keyType = isOriginalField(selectedField?.id) ? "operator" : "renderType";
                props.onSetDraftColumn({
                  ...props.draftColumn,
                  [keyType]: type?.id
                });
              }
            }}
          />
        </>
      )}
    </>
  );
}
